import { faArrowRightFromBracket, faPowerOff, faRotate, faTrash, faWrench } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { downloadCsv, safeName } from '~/team_tools/components/csv'
import { PlayerGlobe } from '~/team_tools/tournaments/PlayerGlobe'
import { PlayerTrendDetail } from '~/team_tools/tournaments/PlayerTrendDetail'
import { toRegions } from '~/team_tools/tournaments/countries'
import { ApprovalQueue } from './ApprovalQueue'
import { DeletedServers } from './DeletedServers'
import { ScheduledServers } from './ScheduledServers'
import { PlayerRows, ago } from './PlayerRows'
import { ServerCard } from './ServerCard'
import {
    ActionRequest,
    BATCH_LOCATIONS,
    Diagnosis,
    Server,
    ServersResponse,
    batchDestroyServers,
    destroyAllServers,
    destroyServer,
    getActions,
    getFleetPlayers,
    getServerHealth,
    getServerPlayers,
    getServers,
    hardRestartServer,
    restartServer,
    stopServer,
    updateServer,
} from './api'
import { mapLabel, zoneLabel, zoneOfSlot } from './maps'
import { DIAGNOSES, STATUS_COPY } from './status'

/**
 * The fleet, as one board.
 *
 * This replaced two tabs -- Servers and Players online -- that were the same
 * list of machines read from two angles, with a click between them to change
 * angle. Here a row is a server; the players are on the row (a count and a
 * curve) and behind it (the roster, with kick and ban). Nothing that either
 * tab could do was dropped: restart, stop, rebuild, destroy, drain, settings,
 * requirements, the approval queue and the fleet-wide destroys are all still
 * here, in the popup or the toolbar.
 *
 * Laid out the way the kill race list is, because the two screens are read
 * the same way: a glance down a list to see what needs attention, then one
 * click into the thing that does. A curve in the gap of every row answers
 * "climbing or emptying" without a click at all.
 */

/** Filter chips, lit. Each diagnosis in its own colour so a lit chip says
 *  what it is without being read; unlit is plain grey. */
const CHIP_ON: Record<Diagnosis, string> = {
    healthy: 'border-green-400/70 bg-green-400/10 text-green-400',
    idle: 'border-white/40 bg-white/10 text-white/80',
    degraded: 'border-amber-400/70 bg-amber-400/10 text-amber-300',
    'not-landing': 'border-red-400/70 bg-red-400/10 text-red-400',
    blocked: 'border-red-400/70 bg-red-400/10 text-red-400',
    offline: 'border-red-400/70 bg-red-400/10 text-red-400',
    dead: 'border-red-500/80 bg-red-500/15 text-red-500',
}
const CHIP_OFF = 'border-white/15 text-white/40 hover:border-white/30 hover:text-white/70'

/** "last report 4m ago", off the heartbeat any server RPC stamps. */
export const lastReport = (iso: string | null | undefined) =>
    iso ? `last report ${ago(Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000)))}` : 'never reported'

/** The diagnosis on a row. Bold red for the three that need a human. */
const DIAG_STYLE: Record<Diagnosis, string> = {
    healthy: 'text-green-400',
    idle: 'text-white/40',
    degraded: 'text-amber-400',
    'not-landing': 'text-red-400 font-bold',
    blocked: 'text-red-400 font-bold',
    offline: 'text-red-400 font-bold',
    dead: 'text-red-500 font-bold',
}

/** The row's edge, in its status colour. The same hue as the chip and the
 *  label, so a glance down the left edge of the list reads the fleet without
 *  reading a word. Strong for the ones that need a hand, quiet for the rest. */
const ROW_EDGE: Record<Diagnosis, string> = {
    healthy: 'border-green-400/40 hover:border-green-400/70',
    idle: 'border-white/15 hover:border-white/35',
    degraded: 'border-amber-400/40 hover:border-amber-400/70',
    'not-landing': 'border-red-400/50 hover:border-red-400/80',
    offline: 'border-red-400/40 hover:border-red-400/70',
    blocked: 'border-red-400/50 hover:border-red-400/80',
    dead: 'border-red-500/60 hover:border-red-500/90',
}

/** The row's own order: what is working first, busiest at the top, and the
 *  broken ones at the bottom where the red edge finds them anyway. */
const RANK: Record<Diagnosis, number> = {
    healthy: 0,
    idle: 1,
    degraded: 2,
    'not-landing': 3,
    offline: 4,
    blocked: 5,
    dead: 6,
}
// A diagnosis the backend adds before this file learns it sorts last instead of NaN.
const rankOf = (d: Diagnosis): number => RANK[d] ?? 99

const ReportingDot = ({ reporting, age }: { reporting: boolean; age: number | null }) => (
    <span
        className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${reporting ? 'bg-green-400' : 'bg-amber-400'}`}
        title={
            reporting
                ? `Stating its own roster${age !== null ? `, ${age}s ago` : ''}`
                : 'Not stating its roster; the count is an estimate'
        }
    />
)

/** On: `/server/<id>/life` and `/server/<id>/purge` exist now
 *  (`team_tools/views/server_life.py`). Kept as a flag rather than deleted,
 *  because switching it back off is the right first move if the life read
 *  turns out to cost more on a real fleet than it does here -- the panel is
 *  a convenience and the board is not. */
const DELETED_SERVERS_READY = true

export const ServersBoard = ({
    profile,
    onClearProfile,
    canEditSchedules = false,
    canOperate = false,
}: {
    /** Show only servers built from this template. Set by the Templates
     *  tab's "running on N" link; shown as a chip here so it can be taken
     *  off again. */
    profile?: { id: number; name: string } | null
    onClearProfile?: () => void
    /** Executive: the scheduled-server toggles write through an Executive-only endpoint. */
    canEditSchedules?: boolean
    /** Developer or executive: every operation here is DeveloperPermission. */
    canOperate?: boolean
} = {}) => {
    const { addNotification } = useNotification()
    const [data, setData] = useState<ServersResponse>({ servers: [], maps: [], regions: [], profiles: [] })
    const [requests, setRequests] = useState<ActionRequest[]>([])
    /** Who is on which server, lowercased, for the search box. One fleet-wide
     *  read that the old Players tab made anyway; here it lets a username in
     *  the search find the one server that player is on. */
    const [rosters, setRosters] = useState<Record<number, string[]>>({})
    const [loading, setLoading] = useState(true)

    const [search, setSearch] = useState('')
    const [diagnoses, setDiagnoses] = useState<Diagnosis[]>([])
    const [regions, setRegions] = useState<string[]>([])

    const [open, setOpen] = useState<number | null>(null)
    /** The roster on its own, from the count on the row. Kick and ban live
     *  here too, because the count is where an eye lands when something on a
     *  server needs dealing with, and the full card is a lot to open for it. */
    const [roster, setRoster] = useState<number | null>(null)
    /** Which server's status is being explained. The legend used to sit
     *  above the whole list; a question about one row wants the answer on
     *  that row, with what this particular server did to earn the label. */
    const [explain, setExplain] = useState<number | null>(null)
    const [confirmDestroyAll, setConfirmDestroyAll] = useState(false)
    const [confirmBatchDestroy, setConfirmBatchDestroy] = useState(false)

    const refresh = useCallback(async () => {
        setLoading(true)
        try {
            // Destroyed servers only feed the Deleted section. While that is
            // switched off, asking for every server ever destroyed on each
            // 30-second refresh cost a diagnosis pass over all of them.
            const [servers, actions] = await Promise.all([getServers(DELETED_SERVERS_READY), getActions()])
            setData(servers)
            setRequests(actions.requests)
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setLoading(false)
        }
        try {
            const fleet = await getFleetPlayers()
            setRosters(
                Object.fromEntries(
                    (fleet.servers ?? []).map((sv) => [
                        sv.id,
                        (sv.players ?? []).map((p) => (p.username ?? '').toLowerCase()),
                    ])
                )
            )
        } catch {
            setRosters({})
        }
    }, [])

    useEffect(() => {
        refresh()
        const timer = setInterval(refresh, 30_000)
        return () => clearInterval(timer)
    }, [refresh])

    // Whole-fleet figures from the unfiltered list. A count taken from a
    // filtered view would understate what "destroy all" is about to do.
    const live = data.servers.filter((s) => s.status === 'active' || s.status === 'inactive')
    const online = live.reduce((n, s) => n + (s.players_count ?? 0), 0)
    const reporting = live.filter((s) => s.reporting).length
    const needsHuman = live.filter((s) => STATUS_COPY[s.diagnosis]?.actionable).length

    const liveByLocation = useMemo(() => {
        const counts: Record<string, number> = { all: live.length }
        for (const s of live) {
            const loc = (s.slot ?? '').split('-')[0]
            if (loc) counts[loc] = (counts[loc] ?? 0) + 1
        }
        return counts
    }, [live])

    const diagCounts = useMemo(() => {
        const c: Record<string, number> = {}
        // Live only, like the list the chips filter: destroyed servers diagnose
        // as dead, so counting them promised rows the filter then hid.
        for (const s of live) c[s.diagnosis] = (c[s.diagnosis] ?? 0) + 1
        return c
    }, [live])

    const regionCounts = useMemo(() => {
        const c: Record<string, number> = {}
        for (const s of live) c[s.region] = (c[s.region] ?? 0) + 1
        return c
    }, [live])

    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase()
        return data.servers
            .filter((s) => {
                if (s.status === 'destroyed') return false
                // Built from that kind: the same rule "running on N" counts by.
                if (profile && s.built_from !== profile.id) return false
                if (diagnoses.length && !diagnoses.includes(s.diagnosis)) return false
                if (regions.length && !regions.includes(s.region)) return false
                if (!needle) return true
                if (
                    [s.slot, s.name, s.host, s.region, s.map_name, String(s.id)]
                        .filter(Boolean)
                        .some((v) => String(v).toLowerCase().includes(needle))
                )
                    return true
                // A username. Two characters or more, or "a" would match
                // every server with anybody on it.
                return needle.length >= 2 && (rosters[s.id] ?? []).some((u) => u.includes(needle))
            })
            .sort(
                (a, b) =>
                    rankOf(a.diagnosis) - rankOf(b.diagnosis) ||
                    (b.players_count ?? 0) - (a.players_count ?? 0) ||
                    (a.name ?? '').localeCompare(b.name ?? '')
            )
    }, [data.servers, search, diagnoses, regions, rosters, profile])

    const toggle = <T,>(list: T[], set: (v: T[]) => void, v: T) =>
        set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

    const filtered = diagnoses.length > 0 || regions.length > 0 || search.trim() !== '' || !!profile

    // Always refreshes, success or failure: a failed action may still have
    // changed something, and a stale board after an error is how people act
    // on wrong data.
    const act = (fn: () => Promise<any>, success: string) => async () => {
        try {
            await fn()
            addNotification(success, 'success')
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            refresh()
        }
    }

    const saveServer = (server: Server) => async (changes: Record<string, any>) => {
        try {
            await updateServer(server.id, changes)
            addNotification(`${server.slot ?? server.name} saved`, 'success')
        } catch (err: any) {
            addNotification(err.message, 'error')
            throw err
        } finally {
            refresh()
        }
    }

    const opened = open !== null ? (data.servers.find((s) => s.id === open) ?? null) : null

    return (
        <div className='flex flex-col gap-3 text-xs font-mono'>
            <ApprovalQueue requests={requests} onChange={refresh} canDecide={canOperate} approveAll />

            {/* The fleet in one line, then the filters. The line is what most
                opens of this screen are for; the filters are for the rest. */}
            <div className='flex flex-row flex-wrap items-baseline gap-x-4 gap-y-1'>
                <span className='text-sm font-bold'>
                    {live.length} <span className='font-normal text-white/50'>servers</span>
                </span>
                <span className='text-sm font-bold text-green-400'>
                    {online} <span className='font-normal text-white/50'>players on</span>
                </span>
                <span className='text-white/40'>
                    {reporting}/{live.length} reporting
                </span>
                {needsHuman > 0 && <span className='font-bold text-red-400'>{needsHuman} need a hand</span>}
                <span className='ml-auto flex flex-row items-center gap-2'>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder='server, map or player name'
                        className='w-44 rounded border border-white/15 bg-transparent px-2 py-0.5 text-xs outline-none focus:border-white/40'
                    />
                    <ActionButton onClick={async () => refresh()} title='Refresh'>
                        <FontAwesomeIcon icon={faRotate} spin={loading} />
                    </ActionButton>
                </span>
            </div>

            <div className='flex flex-row flex-wrap items-center gap-1.5'>
                {DIAGNOSES.map((d) => {
                    const on = diagnoses.includes(d)
                    return (
                        <button
                            key={d}
                            type='button'
                            onClick={() => toggle(diagnoses, setDiagnoses, d)}
                            title={STATUS_COPY[d].summary}
                            className={`rounded border px-2 py-0.5 text-[10px] uppercase tracking-widest transition-colors ${
                                on ? CHIP_ON[d] : CHIP_OFF
                            }`}
                        >
                            {d}
                            <span className='ml-1.5 tabular-nums text-white/30'>{diagCounts[d] ?? 0}</span>
                        </button>
                    )
                })}
                <span className='mx-1 h-3 w-px bg-white/10' />
                {Object.keys(regionCounts)
                    .sort()
                    .map((r) => {
                        const on = regions.includes(r)
                        return (
                            <button
                                key={r}
                                type='button'
                                onClick={() => toggle(regions, setRegions, r)}
                                className={`rounded border px-2 py-0.5 text-[10px] uppercase tracking-widest transition-colors ${
                                    on ? 'border-sky-400/60 bg-sky-400/10 text-sky-300' : CHIP_OFF
                                }`}
                            >
                                {r}
                                <span className='ml-1.5 tabular-nums text-white/30'>{regionCounts[r]}</span>
                            </button>
                        )
                    })}
                <span className='mx-1 h-3 w-px bg-white/10' />
                {profile && (
                    <button
                        type='button'
                        onClick={() => onClearProfile?.()}
                        title='Showing only servers built from this template. Click to show all.'
                        className='rounded border border-violet-400/60 bg-violet-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-violet-300'
                    >
                        template: {profile.name} ×
                    </button>
                )}
                <button
                    type='button'
                    onClick={() => {
                        setDiagnoses([])
                        setRegions([])
                        setSearch('')
                        onClearProfile?.()
                    }}
                    disabled={!filtered}
                    className='ml-1 rounded border border-white/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-white/40 hover:border-white/30 hover:text-white disabled:opacity-25 disabled:hover:border-white/15 disabled:hover:text-white/40'
                >
                    clear all
                </button>

                {/* The two fleet-wide destroys, at the far end where a hand
                    reaching for a filter will not land on them. */}
                {live.length > 0 && (
                    <span className='ml-auto flex flex-row gap-2'>
                        <ActionButton
                            onClick={async () => setConfirmBatchDestroy(true)}
                            disabled={!canOperate}
                            className='text-red-400/80'
                            title='Run the batch destroy workflow on GitHub for a whole location'
                        >
                            batch destroy…
                        </ActionButton>
                        <ActionButton
                            onClick={async () => setConfirmDestroyAll(true)}
                            disabled={!canOperate}
                            className='text-red-500'
                            title='Destroy every server in the fleet'
                        >
                            destroy all…
                        </ActionButton>
                    </span>
                )}
            </div>

            {confirmDestroyAll && (
                <ConfirmBar
                    message={`Destroy all ${live.length} servers?`}
                    detail='Raises one approval request covering the whole fleet. Nothing happens until it is approved above.'
                    confirmLabel={`Request destroy of ${live.length}`}
                    onCancel={() => setConfirmDestroyAll(false)}
                    onConfirm={async () => {
                        setConfirmDestroyAll(false)
                        await act(() => destroyAllServers(live.length), 'Destroy-all request raised')()
                    }}
                />
            )}
            {confirmBatchDestroy && (
                <ConfirmBar
                    message='Batch destroy which location?'
                    detail='Runs the batch destroy workflow on GitHub. The count is what the workflow will be told to expect.'
                    confirmLabel='Run workflow'
                    options={BATCH_LOCATIONS.map((loc) => ({
                        value: loc,
                        label: `${loc} — ${liveByLocation[loc] ?? 0} known live server(s)`,
                        danger: true,
                    }))}
                    defaultOption='all'
                    onCancel={() => setConfirmBatchDestroy(false)}
                    onConfirm={async (loc) => {
                        setConfirmBatchDestroy(false)
                        await act(
                            () => batchDestroyServers(loc, liveByLocation[loc] ?? 0),
                            `Batch destroy dispatched for ${loc}`
                        )()
                    }}
                />
            )}

            {/* ---- the rows ------------------------------------------------ */}
            <div className='flex flex-col gap-2'>
                {visible.map((s) => {
                    const destroyed = s.status === 'destroyed'
                    return (
                        <div
                            key={s.id}
                            role='button'
                            tabIndex={0}
                            onClick={() => setOpen(s.id)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    setOpen(s.id)
                                }
                            }}
                            className={`grid cursor-pointer grid-cols-[14rem,minmax(0,1fr),4.5rem,6rem,15rem] items-center gap-3 rounded border px-3 py-2 ${
                                destroyed ? 'border-white/10 opacity-40' : ROW_EDGE[s.diagnosis]
                            }`}
                        >
                            <div className='flex min-w-0 flex-col'>
                                <span className={`truncate font-bold ${destroyed ? 'line-through' : ''}`}>
                                    {s.name ?? <span className='font-normal italic text-white/50'>unconfigured</span>}
                                </span>
                                <span className='truncate text-white/40'>
                                    {s.slot ?? `#${s.id}`} ·{' '}
                                    <span title={`Slot range ${s.slot ?? '?'}; reports to the game as ${s.region}`}>
                                        {zoneLabel(zoneOfSlot(s.slot)) ?? s.region}
                                    </span>{' '}
                                    · {mapLabel(s.map_name)} · {s.fee ? `$${s.fee}` : 'free'}
                                </span>
                            </div>

                            {/* The row's sparkline read /servers/activity, a route
                                that never existed: a 404 every 30 seconds and an
                                empty curve on every row. The column is kept so the
                                grid stays aligned; the server sheet has the real
                                chart. */}
                            <div className='hidden min-w-0 sm:block' aria-hidden />

                            <button
                                type='button'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setRoster(s.id)
                                }}
                                disabled={destroyed}
                                title='Who is on it, with kick and ban'
                                className='rounded border border-transparent px-1 text-right hover:border-white/25 hover:bg-white/5 disabled:hover:border-transparent disabled:hover:bg-transparent'
                            >
                                <div className={`tabular-nums ${s.players_count > 0 ? 'text-white' : 'text-white/40'}`}>
                                    {s.players_count}
                                    <span className='text-white/30'>{s.max_players ? `/${s.max_players}` : ''}</span>
                                </div>
                                <div className='flex flex-row items-center justify-end gap-1 text-white/30'>
                                    <ReportingDot reporting={s.reporting} age={s.roster_age_seconds} />
                                    on
                                </div>
                            </button>

                            <span className='flex flex-row items-center justify-end gap-1.5'>
                                <span
                                    className={`uppercase tracking-wide ${DIAG_STYLE[s.diagnosis]}`}
                                    title={s.diagnosis_reasons?.join('\n') || s.diagnosis}
                                >
                                    {destroyed ? 'destroyed' : s.diagnosis}
                                </span>
                                {/* D-095: a server stays "live" through up to six silent
                                    hours (idle servers are normal), so say how long the
                                    silence has been rather than inventing a new state. */}
                                {!destroyed && (
                                    <span className='text-[10px] normal-case text-white/40' title='Time since this server last reported to the backend'>
                                        {lastReport(s.last_heartbeat_at)}
                                    </span>
                                )}
                                {!destroyed && (
                                    <button
                                        type='button'
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setExplain(s.id)
                                        }}
                                        title='What this status means'
                                        className='flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-white/25 text-[9px] font-bold text-white/50 hover:border-white/60 hover:text-white'
                                    >
                                        i
                                    </button>
                                )}
                            </span>

                            <div className='flex flex-row justify-end gap-1' onClick={(e) => e.stopPropagation()}>
                                {!destroyed && (
                                    <>
                                        {s.status === 'active' && (
                                            <ActionButton
                                                onClick={async () => setOpen(s.id)}
                                                title='Drain: finish the matches on it, then exit (opens the server)'
                                                className='text-green-400'
                                            >
                                                <FontAwesomeIcon icon={faArrowRightFromBracket} />
                                            </ActionButton>
                                        )}
                                        <ActionButton
                                            onClick={async () => setOpen(s.id)}
                                            disabled={!s.slot}
                                            title='Restart the game container (opens the server)'
                                            className='text-blue-400'
                                        >
                                            <FontAwesomeIcon icon={faRotate} />
                                        </ActionButton>
                                        {s.status === 'active' && (
                                            <ActionButton
                                                onClick={async () => setOpen(s.id)}
                                                title='Stop taking players (opens the server)'
                                                className='text-white/60'
                                            >
                                                <FontAwesomeIcon icon={faPowerOff} />
                                            </ActionButton>
                                        )}
                                        <ActionButton
                                            onClick={async () => setOpen(s.id)}
                                            disabled={!s.slot}
                                            title='Rebuild the machine (opens the server)'
                                            className='text-amber-400'
                                        >
                                            <FontAwesomeIcon icon={faWrench} />
                                        </ActionButton>
                                        <ActionButton
                                            onClick={async () => setOpen(s.id)}
                                            title='Destroy (opens the server)'
                                            className='text-red-500'
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </ActionButton>
                                    </>
                                )}
                            </div>
                        </div>
                    )
                })}
                {!visible.length && (
                    <div className='py-6 text-center text-white/30'>
                        {loading ? 'loading…' : filtered ? 'Nothing matches that filter.' : 'No servers.'}
                    </div>
                )}
            </div>

            {/* Writes are Executive-only on the server; showing live toggles to
                everyone else just produced 403s. */}
            <ScheduledServers canEdit={canEditSchedules} />

            {/* Both routes exist now. The report is the same arithmetic a
                live tournament already shows -- fees by currency off
                `GameTransaction`, concurrency off the spawned/closed overlap --
                keyed to one server over its whole life instead of to a race
                over its window. */}
            {DELETED_SERVERS_READY && (
                <DeletedServers servers={data.servers.filter((s) => s.status === 'destroyed')} onChanged={refresh} />
            )}

            <span className='text-[11px] text-white/30'>
                New servers are made on the Make servers tab, so they arrive already configured.
            </span>

            {explain !== null && (
                <StatusSheet server={data.servers.find((s) => s.id === explain)!} onClose={() => setExplain(null)} />
            )}

            {roster !== null && (
                <RosterSheet
                    server={data.servers.find((s) => s.id === roster)!}
                    onClose={() => setRoster(null)}
                    onChanged={refresh}
                />
            )}

            {/* ---- one server ---------------------------------------------- */}
            {opened && (
                <ServerSheet
                    server={opened}
                    maps={data.maps}
                    canOperate={canOperate}
                    onClose={() => setOpen(null)}
                    onSave={saveServer(opened)}
                    onRestart={(refund: boolean) =>
                        act(
                            () => restartServer(opened.id, 'server', refund),
                            refund
                                ? `Restart dispatched for ${opened.slot}; spawn fees refunded`
                                : `Restart dispatched for ${opened.slot}`
                        )()
                    }
                    onHardRestart={(refund: boolean) =>
                        act(
                            () => hardRestartServer(opened.id, refund),
                            `Rebuilding ${opened.slot}. It will leave the fleet until the new machine registers`
                        )()
                    }
                    onStop={(refund: boolean) =>
                        act(
                            () => stopServer(opened.id, refund),
                            `${opened.slot} stopped. It is no longer taking players`
                        )()
                    }
                    onDestroy={() => act(() => destroyServer(opened.id), 'Destroy request raised, approve it above')()}
                />
            )}
        </div>
    )
}

/**
 * Everything about one server, in the order it gets asked: where the players
 * are, how many over the last few hours, then the card -- health, drain,
 * details, settings, requirements, the roster with kick and ban, and the
 * restart / stop / rebuild / destroy controls. The card is the same component
 * the old tab expanded inline; only its title line is gone, because this
 * header is it.
 */
const ServerSheet = ({
    server,
    maps,
    onClose,
    canOperate,
    ...handlers
}: {
    server: Server
    maps: ServersResponse['maps']
    canOperate: boolean
    onClose: () => void
    onSave: (changes: Record<string, any>) => Promise<void>
    onRestart: (refund: boolean) => Promise<void>
    onHardRestart: (refund: boolean) => Promise<void>
    onStop: (refund: boolean) => Promise<void>
    onDestroy: () => Promise<void>
}) => {
    const [health, setHealth] = useState<any | null>(null)

    const reloadHealth = useCallback(async () => {
        try {
            setHealth(await getServerHealth(server.id))
        } catch {
            setHealth(null)
        }
    }, [server.id])

    // The only /health poll for an open sheet. The card inside used to run its
    // own on the same endpoint, and activity_series is the expensive part of
    // it, so every open sheet cost two.
    useEffect(() => {
        reloadHealth()
        const timer = setInterval(reloadHealth, 30_000)
        return () => clearInterval(timer)
    }, [reloadHealth])

    const points: number[] = health?.activity?.points?.map((p: any) => p.players) ?? []
    const first = health?.activity?.points?.[0]?.t
    const last = health?.activity?.points?.[health.activity.points.length - 1]?.t
    const regions = health?.regions ? toRegions(health.regions) : []

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className='flex max-h-[88vh] w-full max-w-3xl flex-col gap-4 overflow-auto rounded border border-white/20 bg-zinc-900 p-4 text-xs font-mono text-white'
            >
                <div className='flex flex-row items-baseline justify-between gap-3'>
                    <span className='flex min-w-0 flex-row items-baseline gap-2'>
                        <span className='truncate text-sm font-bold'>
                            {server.name ?? server.slot ?? `#${server.id}`}
                        </span>
                        <span className='text-white/40'>
                            {server.slot} · {server.region} · {mapLabel(server.map_name)}
                        </span>
                        <span className={`uppercase tracking-wide ${DIAG_STYLE[server.diagnosis]}`}>
                            {server.diagnosis}
                        </span>
                    </span>
                    <span className='flex shrink-0 flex-row items-center gap-3'>
                        <button
                            type='button'
                            disabled={!health}
                            onClick={() =>
                                downloadCsv(`${safeName(server.slot ?? server.name ?? String(server.id))}-health`, [
                                    ['server', server.name ?? ''],
                                    ['slot', server.slot ?? ''],
                                    ['region', server.region],
                                    ['map', server.map_name ?? ''],
                                    ['diagnosis', server.diagnosis],
                                    ['players', health?.signals?.online ?? ''],
                                    ['in_session', health?.signals?.playing ?? ''],
                                    ['sessions_30m', health?.signals?.recent_sessions_30m ?? ''],
                                    ['kills_30m', health?.signals?.recent_kills_30m ?? ''],
                                    [],
                                    ['bucket_start', 'players', 'kills'],
                                    ...((health?.activity?.points ?? []) as any[]).map((p) => [
                                        p.t,
                                        p.players,
                                        p.kills,
                                    ]),
                                    [],
                                    ['country', 'players'],
                                    ...((health?.regions ?? []) as any[]).map((r) => [
                                        r.country_code ?? r.country_name,
                                        r.players,
                                    ]),
                                ])
                            }
                            className='text-white/40 underline hover:text-white disabled:opacity-30'
                        >
                            csv
                        </button>
                        <button type='button' onClick={onClose} className='text-white/40 underline hover:text-white'>
                            close
                        </button>
                    </span>
                </div>

                {regions.length > 0 && <PlayerGlobe regions={regions} live={server.diagnosis === 'healthy'} />}

                {points.length > 1 && first && last && (
                    <PlayerTrendDetail
                        points={points}
                        startsAt={first}
                        endsAt={last}
                        bucket={health?.activity?.bucket_minutes}
                        live={server.diagnosis === 'healthy'}
                    />
                )}

                <ServerCard
                    server={server}
                    maps={maps}
                    canEdit={canOperate}
                    canOperate={canOperate}
                    sharedHealth={{ health, reload: reloadHealth }}
                    expanded={true}
                    variant='sheet'
                    onToggle={() => {}}
                    {...handlers}
                />
            </div>
        </div>
    )
}

/** Just the players on one server, with kick and ban. The same rows the full
 *  card shows, without the rest of the card around them. */
const RosterSheet = ({
    server,
    onClose,
    onChanged,
}: {
    server: Server
    onClose: () => void
    onChanged: () => void
}) => {
    const [players, setPlayers] = useState<any[] | null>(null)
    const [asOf, setAsOf] = useState<string | null>(null)

    useEffect(() => {
        let alive = true
        const load = async () => {
            try {
                const d = await getServerPlayers(server.id)
                if (alive) {
                    setPlayers(d.players ?? [])
                    setAsOf(d.as_of ?? null)
                }
            } catch {
                if (alive) setPlayers([])
            }
        }
        load()
        const timer = setInterval(load, 15_000)
        return () => {
            alive = false
            clearInterval(timer)
        }
    }, [server.id])

    const playing = players?.filter((p) => p.in_match).length ?? 0

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className='flex max-h-[80vh] w-full max-w-2xl flex-col gap-3 overflow-auto rounded border border-white/20 bg-zinc-900 p-4 text-xs font-mono text-white'
            >
                <div className='flex flex-row items-baseline justify-between gap-3'>
                    <span className='flex min-w-0 flex-row items-baseline gap-2'>
                        <span className='truncate text-sm font-bold'>
                            {server.name ?? server.slot ?? `#${server.id}`}
                        </span>
                        <span className='text-white/40'>
                            {players ? `${players.length} on · ${playing} in a match` : '…'}
                            {server.max_players ? ` · ${server.max_players} max` : ''}
                        </span>
                        {!server.reporting && (
                            <span
                                className='text-amber-400'
                                title='This server is not stating its roster; this list is assembled from join reservations'
                            >
                                estimate
                            </span>
                        )}
                    </span>
                    <span className='flex shrink-0 flex-row items-center gap-3'>
                        <button
                            type='button'
                            disabled={!players?.length}
                            onClick={() =>
                                downloadCsv(`${safeName(server.slot ?? server.name ?? String(server.id))}-players`, [
                                    [
                                        'user_id',
                                        'username',
                                        'in_match',
                                        'joined_at',
                                        'last_seen_at',
                                        'last_action',
                                        'detail',
                                    ],
                                    ...(players ?? []).map((p) => [
                                        p.user_id,
                                        p.username,
                                        p.in_match ? 'yes' : 'no',
                                        p.joined_at,
                                        p.last_seen_at,
                                        p.last_action?.type ?? '',
                                        p.last_action?.detail ?? '',
                                    ]),
                                ])
                            }
                            className='text-white/40 underline hover:text-white disabled:opacity-30'
                        >
                            csv
                        </button>
                        <button type='button' onClick={onClose} className='text-white/40 underline hover:text-white'>
                            close
                        </button>
                    </span>
                </div>

                {players === null ? (
                    <div className='text-white/30'>Loading…</div>
                ) : players.length === 0 ? (
                    <div className='text-white/30'>Nobody is on it.</div>
                ) : (
                    <PlayerRows players={players} serverId={server.id} onBanned={onChanged} />
                )}

                {asOf && (
                    <span className='text-[10px] text-white/30'>
                        as of {new Date(asOf).toLocaleTimeString()} · refreshes every 15s
                    </span>
                )}
            </div>
        </div>
    )
}

/** What a status means in general, and what this server did to earn it.
 *  The general part is the legend's own copy; the specific part is the
 *  diagnosis reasons the backend attached to this row. */
const StatusSheet = ({ server, onClose }: { server: Server; onClose: () => void }) => {
    const copy = STATUS_COPY[server.diagnosis]
    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className='flex w-full max-w-lg flex-col gap-3 rounded border border-white/20 bg-zinc-900 p-4 text-xs font-mono text-white'
            >
                <div className='flex flex-row items-baseline justify-between gap-3'>
                    <span className='flex min-w-0 flex-row items-baseline gap-2'>
                        <span className={`text-sm font-bold uppercase tracking-wide ${DIAG_STYLE[server.diagnosis]}`}>
                            {copy?.title ?? server.diagnosis}
                        </span>
                        <span className='truncate text-white/40'>{server.name ?? server.slot}</span>
                    </span>
                    <button
                        type='button'
                        onClick={onClose}
                        className='shrink-0 text-white/40 underline hover:text-white'
                    >
                        close
                    </button>
                </div>

                {copy && (
                    <>
                        <div className='text-white/90'>{copy.summary}</div>
                        <div className='leading-relaxed text-white/60'>{copy.detail}</div>
                        <div className={copy.actionable ? 'text-red-400' : 'text-white/40'}>
                            {copy.actionable ? 'Needs a human.' : 'Nothing to do.'}
                        </div>
                    </>
                )}

                {server.diagnosis_reasons?.length > 0 && (
                    <div className='flex flex-col gap-1 border-t border-white/10 pt-3'>
                        <span className='text-[10px] uppercase tracking-widest text-white/40'>On this server</span>
                        {server.diagnosis_reasons.map((r, i) => (
                            <span key={i} className='text-white/80'>
                                · {r}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
